import { API_BASE, MAIN_API_BASE } from './apiConfig';

const fetchApi = async (endpoint, options = {}) => {
  try {
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    return await response.json();
  } catch (error) {
    console.error(`API Error on ${endpoint}:`, error);
    return { status: false, message: error.message };
  }
};

const fetchMultipart = async (endpoint, formData) => {
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
    });
    return await response.json();
  } catch (error) {
    console.error(`API Error on ${endpoint}:`, error);
    return { status: false, message: error.message };
  }
};

// Dashboard
export const getDashboardStats = async (userProfile) => {
  return fetchApi(`${API_BASE}/dashboard/stats.php`);
};

// Properties
export const getProperties = async (params = {}) => {
  const { filters = {}, ...rest } = params;
  const combined = { ...filters, ...rest };
  const cleanParams = {};
  Object.keys(combined).forEach(k => {
    if (combined[k] !== undefined && combined[k] !== null && combined[k] !== '') {
      cleanParams[k] = combined[k];
    }
  });
  const query = new URLSearchParams(cleanParams).toString();
  return fetchApi(`${API_BASE}/properties/list.php?${query}`);
};

export const createProperty = async (data) => {
  if (data instanceof FormData) return fetchMultipart(`${API_BASE}/properties/add.php`, data);
  return fetchApi(`${API_BASE}/properties/add.php`, { method: 'POST', body: JSON.stringify(data) });
};
export const updateProperty = async (id, data) => {
  if (data instanceof FormData) {
    if (!data.has('id')) data.append('id', id);
    if (!data.has('property_id')) data.append('property_id', id);
    return fetchMultipart(`${API_BASE}/properties/update.php`, data);
  }
  return fetchApi(`${API_BASE}/properties/update.php`, { method: 'POST', body: JSON.stringify({ id, property_id: id, ...data }) });
};
export const deleteProperty = async (id) => fetchApi(`${API_BASE}/properties/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });
export const markPropertySold = async (id, soldTo = '', listingType = 'sell') => fetchApi(`${API_BASE}/properties/mark_sold.php`, { method: 'POST', body: JSON.stringify({ property_id: id, id, sold_to: soldTo, listing_type: listingType }) });
export const updatePropertyStatus = async (id, status) => fetchApi(`${API_BASE}/properties/update_status.php`, { method: 'POST', body: JSON.stringify({ id, status }) });
export const bulkUpdateProperties = async (ids, data) => fetchApi(`${API_BASE}/properties/bulk_update.php`, { method: 'POST', body: JSON.stringify({ ids, data }) });
export const getUserBuildings = async (userId) => {
  const url = userId 
    ? `${MAIN_API_BASE}/properties/get_user_buildings.php?user_id=${userId}`
    : `${MAIN_API_BASE}/properties/get_user_buildings.php`;
  return fetchApi(url);
};

// Homestays
export const getHomestays = async (params = {}) => {
  const cleanParams = {};
  Object.keys(params).forEach(k => {
    if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
      cleanParams[k] = params[k];
    }
  });
  const query = new URLSearchParams(cleanParams).toString();
  return fetchApi(`${API_BASE}/homestays/list.php?${query}`);
};
export const updateHomestayStatus = async (id, status) => fetchApi(`${API_BASE}/homestays/update_status.php`, { method: 'POST', body: JSON.stringify({ id, status }) });
export const deleteHomestay = async (id) => fetchApi(`${API_BASE}/homestays/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });
export const getHomestayBookings = async (homestayId, page = 1) => fetchApi(`${API_BASE}/homestays/bookings.php?homestay_id=${homestayId}&page=${page}`);
export const updateHomestayBookingStatus = async (bookingId, status) => fetchApi(`${API_BASE}/homestays/update_booking_status.php`, { method: 'POST', body: JSON.stringify({ booking_id: bookingId, status }) });


// Rent & Tenant Management
export const getManageProperty = async (propertyId) => fetchApi(`${API_BASE}/properties/get_manage_property.php?property_id=${propertyId}`);
export const addTenant = async (data) => fetchMultipart(`${API_BASE}/properties/add_tenant.php`, data); // Requires formData for document uploads
export const vacateTenant = async (dataOrId, propId) => {
  const payload = (typeof dataOrId === 'object' && dataOrId !== null)
    ? dataOrId
    : { tenant_id: dataOrId, property_id: propId };
  return fetchApi(`${API_BASE}/properties/vacate_tenant.php`, { method: 'POST', body: JSON.stringify(payload) });
};
export const getPgOccupants = async (propertyId) => fetchApi(`${API_BASE}/properties/get_pg_occupants.php?property_id=${propertyId}`);
export const addPgBedOccupant = async (data) => fetchMultipart(`${API_BASE}/properties/add_bed_occupant.php`, data); // Requires formData
export const vacatePgBed = async (roomId, bedNumber) => fetchApi(`${API_BASE}/properties/vacate_bed.php`, { method: 'POST', body: JSON.stringify({ room_id: roomId, bed_number: bedNumber }) });
export const getRentLedger = async (tenantId, tenantType = 'property') => fetchApi(`${API_BASE}/properties/get_rent_ledger.php?tenant_id=${tenantId}&tenant_type=${tenantType}`);
export const addRentLedger = async (data) => fetchApi(`${API_BASE}/properties/add_rent_ledger.php`, { method: 'POST', body: JSON.stringify(data) });

// Users / Sellers
export const getUsers = async () => fetchApi(`${API_BASE}/users/list.php`);
export const getAppUsers = async () => fetchApi(`${API_BASE}/users/users_list.php`);
export const createUser = async (data) => fetchApi(`${API_BASE}/users/register.php`, { method: 'POST', body: JSON.stringify(data) });

export const updateUser = async (uid, data) => fetchApi(`${API_BASE}/users/update.php`, { method: 'POST', body: JSON.stringify({ uid, ...data }) });
export const updateUserRole = async (uid, role) => fetchApi(`${API_BASE}/users/update_role.php`, { method: 'POST', body: JSON.stringify({ uid, role }) });
export const deleteUserProfile = async (uid) => fetchApi(`${API_BASE}/users/delete.php`, { method: 'POST', body: JSON.stringify({ uid }) });

// Leads / Callback Requests
export const getLeads = async (params = {}) => {
  const cleanParams = {};
  Object.keys(params).forEach(k => {
    if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
      cleanParams[k] = params[k];
    }
  });
  const query = new URLSearchParams(cleanParams).toString();
  return fetchApi(`${API_BASE}/leads/list.php${query ? `?${query}` : ''}`);
};
export const updateLeadStatus = async (id, status) => fetchApi(`${API_BASE}/leads/update_status.php`, { method: 'POST', body: JSON.stringify({ id, leadId: id, status }) });

// Chats
export const getChats = async () => {
  const res = await fetchApi(`${API_BASE}/chat/list.php`);
  return res?.chats || [];
};
export const getChatMessages = async (chatId) => fetchApi(`${API_BASE}/chat/messages.php?chat_id=${chatId}`);
export const sendAdminMessage = async (chatId, message) => fetchApi(`${API_BASE}/chat/send_message.php`, { method: 'POST', body: JSON.stringify({ chat_id: chatId, message }) });
export const editAdminMessage = async (messageId, newMessage) => fetchApi(`${API_BASE}/chat/edit_message.php`, { method: 'POST', body: JSON.stringify({ message_id: messageId, new_message: newMessage }) });
export const deleteAdminMessage = async (messageId) => fetchApi(`${API_BASE}/chat/delete_message.php`, { method: 'POST', body: JSON.stringify({ message_id: messageId }) });
// Transactions
export const getTransactions = async (params = {}) => {
  const cleanParams = {};
  Object.keys(params).forEach(k => {
    if (params[k] !== undefined && params[k] !== null && params[k] !== '') {
      cleanParams[k] = params[k];
    }
  });
  const query = new URLSearchParams(cleanParams).toString();
  return fetchApi(`${API_BASE}/transactions/list.php${query ? `?${query}` : ''}`);
};

// Areas / Districts
export const getAreas = async (state = '') => fetchApi(`${API_BASE}/areas/list.php${state ? `?state=${encodeURIComponent(state)}` : ''}`);
export const createArea = async (data) => fetchApi(`${API_BASE}/areas/add.php`, { method: 'POST', body: JSON.stringify(data) });
export const updateArea = async (id, data) => fetchApi(`${API_BASE}/areas/update.php`, { method: 'POST', body: JSON.stringify({ id, ...data }) });
export const deleteArea = async (id) => fetchApi(`${API_BASE}/areas/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });

// Notes
export const getNotes = async (propertyId) => [];
export const addNote = async (propertyId, note) => fetchApi(`${API_BASE}/notes/add.php`, { method: 'POST', body: JSON.stringify({ propertyId, note }) });

// Reports
export const getReportsSummary = async () => fetchApi(`${API_BASE}/reports/summary.php`);
export const exportReportData = async (type) => fetchApi(`${API_BASE}/reports/export.php?type=${type}`);

// Banners
export const getBanners = async () => fetchApi(`${API_BASE}/banners/list.php`);
export const addBanner = async (formData) => fetchMultipart(`${API_BASE}/banners/add.php`, formData);
export const updateBanner = async (formData) => fetchMultipart(`${API_BASE}/banners/update.php`, formData);
export const deleteBanner = async (id) => fetchApi(`${API_BASE}/banners/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });

// Banner Ads
export const getBannerAds = async () => fetchApi(`${API_BASE}/banner_ads/list.php`);
export const addBannerAd = async (formData) => fetchMultipart(`${API_BASE}/banner_ads/add.php`, formData);
export const updateBannerAd = async (formData) => fetchMultipart(`${API_BASE}/banner_ads/update.php`, formData);
export const deleteBannerAd = async (id) => fetchApi(`${API_BASE}/banner_ads/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });

// Plans
export const getAdminPlans = async () => fetchApi(`${API_BASE}/plans/list.php`);
export const addPlan = async (data) => fetchApi(`${API_BASE}/plans/add.php`, { method: 'POST', body: JSON.stringify(data) });
export const updatePlan = async (id, data) => fetchApi(`${API_BASE}/plans/update.php`, { method: 'POST', body: JSON.stringify({ id, ...data }) });
export const deletePlan = async (id) => fetchApi(`${API_BASE}/plans/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });
export const togglePlanStatus = async (id, is_active) => fetchApi(`${API_BASE}/plans/toggle_status.php`, { method: 'POST', body: JSON.stringify({ id, is_active }) });

// Helpdesk
export const getAdminHelpdeskTickets = async () => fetchApi(`${API_BASE}/helpdesk/list.php`);
export const updateTicketStatus = async (id, status) => fetchApi(`${API_BASE}/helpdesk/update_status.php`, { method: 'POST', body: JSON.stringify({ id, status }) });
export const deleteTicket = async (id) => fetchApi(`${API_BASE}/helpdesk/delete.php`, { method: 'POST', body: JSON.stringify({ id }) });
